import { createSelector } from "reselect"
import { chain, map, get } from "lodash"
import { getMissingDataFiles, betasAreMissing } from "selectors/missingDataFiles"
import { getGroup, menuContentSets } from "helpers/menu"
import { getForecasts } from "modules/portfolio-root/selectors/forecast"
import { getIsOpPro } from "modules/account-root/selectors"

const getContentSets = (_, props) => props.contentSets
const getModelId = (_, props) => props.modelId
const getMenuOverride = (_, props) => props.menuItems
export const getNormalization = (state) => get(state, "modules.analyzeHeader.normalization")
export const getSelectedTypeId = (_, props) => props.selectedId
export const getFiltersVisible = state => state.modules.analyzeHeader.filtersVisible
export const getHasUnexpandedComposites = state => get(state, "global.data.hasUnexpandedComposites.data", false)
export const getIsActive = (_, props) => get(props, "isActive")
export const getStartDate = (_, props) => get(props, "startDate")
export const getAiSummaryStartDate = (_, props) => get(props, "aiSummaryStartDate")

const getAnalyzeTypes = createSelector(
  [getContentSets, getMissingDataFiles, getModelId, getForecasts, getIsOpPro, getMenuOverride],
  (contentSets, missingDataFiles, modelId, forecasts, isOpPro, menuOverride) => {
    if (menuOverride) {
      return menuOverride
    }
    const missingBetas = betasAreMissing(missingDataFiles, modelId)
    return [
      {
        id: "performance",
        name: "Performance",
        base: true,
        tooltip: "Ex-post performance with factor attribution",
        path: "performance",
      },
      {
        id: "risk",
        name: "Predictive Risk",
        base: true,
        disabled: isOpPro,
        tooltip: isOpPro ? "Only available with enterprise plan" : "Ex-ante total risk & factor risk decomposition",
        path: "risk",
      },
      {
        id: "exposure",
        name: "Exposure",
        base: true,
        tooltip: "Weighted-average factor exposure",
        path: "exposure",
      },
      {
        id: "composition",
        name: "Composition",
        base: true,
        disabled: missingBetas,
        tooltip: missingBetas ? "There are no Predicted or Historical beta values for the selected model" : "Beta as provided by the selected risk model",
        path: "composition",
      },
      {
        type: "factorSensitivity",
        id: "predictedFactorSensitivity",
        name: "Predicted Factor Sensitivity",
        path: "factor-sensitivity/predicted",
        base: true,
        disabled: isOpPro,
        tooltip: isOpPro
          ? "Only available with enterprise plan"
          : "Expected portfolio return impact from factors based on factor exposure and expected factor volatility. Factor volatility is estimated using the risk model covariance matrix.",
      },
      {
        type: "factorSensitivity",
        id: "historicalFactorSensitivity",
        name: "Historical Factor Sensitivity",
        path: "factor-sensitivity/historical",
        base: true,
        tooltip: "Expected portfolio return impact from factors based on factor exposure and expected factor volatility. Factor volatility is estimated using historical realized factor returns.",
      },
      ...map(forecasts, (forecast) => ({ ...forecast, type: "forecast", path: `forecast/${forecast.id}` })),
      ...menuContentSets(contentSets),
    ]
  },
)

export const getSelectedTypeName = createSelector(
  [getAnalyzeTypes, getSelectedTypeId],
  (analyzeTypes, selectedTypeId) => chain(analyzeTypes)
    .find(t => t.id === selectedTypeId)
    .get("name", selectedTypeId)
    .value(),
)

export const getAnalyzeMenuItems = createSelector(
  [getAnalyzeTypes],
  (types) => map(types, (type, i) => ({
    order: i,
    id: type.id,
    label: type.name,
    ...getGroup(type),
    ...type,
  })),
)
